/**
 * Artifact catalogue for the Project builder.
 * Picker routes are the authorized selectors from the Project handoff.
 * `href` opens the resource's own screen; Project management does not
 * grant access there.
 */

export const ARTIFACT_CATALOG = [
  {
    elementType: 'dynamic-entity',
    entityKind: 'entity',
    collection: 'dynamicEntities',
    pickerRoute: 'dynamicEntity/paged',
    select: 'id,displayName',
    labelField: 'displayName',
    orderBy: 'displayName',
    href: (id) => `/entities/${id}/builder`,
  },
  {
    elementType: 'custom-list',
    entityKind: 'list',
    collection: 'customLists',
    pickerRoute: 'customList/paged',
    select: 'id,name,type',
    labelField: 'name',
    orderBy: 'name',
    href: (id) => `/custom-lists/${id}/builder`,
  },
  {
    elementType: 'process',
    entityKind: 'process',
    collection: 'processes',
    pickerRoute: 'process/allowed',
    select: 'id,name',
    labelField: 'name',
    orderBy: 'name',
    href: (id) => `/processes/${id}/builder`,
  },
  {
    elementType: 'app',
    entityKind: 'app',
    collection: 'apps',
    pickerRoute: 'app/allowed',
    select: 'id,name',
    labelField: 'name',
    orderBy: 'name',
    href: (id) => `/apps/${id}/builder`,
  },
  {
    elementType: 'ai-agent',
    entityKind: 'aiAgent',
    collection: 'aiAgents',
    pickerRoute: 'ai/agent/allowed',
    select: 'id,name,iconUrl,isActive',
    labelField: 'name',
    orderBy: 'name',
    href: (id) => `/agents/${id}/builder`,
  },
  {
    elementType: 'ai-skill',
    entityKind: 'skill',
    collection: 'aiSkills',
    pickerRoute: 'ai/skill/allowed',
    select: 'id,name,packageName',
    labelField: 'name',
    orderBy: 'name',
    href: () => '/agents?type=skills',
  },
  {
    elementType: 'area',
    entityKind: 'area',
    collection: 'areas',
    pickerRoute: 'area/paged',
    select: 'id,name',
    labelField: 'name',
    orderBy: 'name',
    href: () => '/configuration/areas',
  },
  {
    elementType: 'position',
    entityKind: 'position',
    collection: 'positions',
    pickerRoute: 'position/paged',
    select: 'id,name',
    labelField: 'name',
    orderBy: 'name',
    href: () => '/configuration/positions',
  },
  {
    elementType: 'user-group',
    entityKind: 'userGroup',
    collection: 'userGroups',
    pickerRoute: 'userGroup/paged',
    select: 'id,name',
    labelField: 'name',
    orderBy: 'name',
    href: () => '/configuration/users-groups',
  },
];

export const getArtifactConfig = (elementType) =>
  ARTIFACT_CATALOG.find((item) => item.elementType === elementType) || null;

const EMPTY_ARTIFACTS = {
  apps: [],
  dynamicEntities: [],
  customLists: [],
  processes: [],
  aiAgents: [],
  aiSkills: [],
  areas: [],
  positions: [],
  userGroups: [],
};

export const normalizeArtifacts = (artifacts) => {
  const source = artifacts || {};
  return Object.keys(EMPTY_ARTIFACTS).reduce((acc, key) => {
    acc[key] = Array.isArray(source[key]) ? source[key] : [];
    return acc;
  }, {});
};

const LIST_TYPE_LABEL_IDS = {
  list: 'projectBuilder.listType.list',
  gantt: 'projectBuilder.listType.gantt',
  calendar: 'projectBuilder.listType.calendar',
  'task-list': 'projectBuilder.listType.taskList',
  tasklist: 'projectBuilder.listType.taskList',
  'task-board': 'projectBuilder.listType.taskBoard',
  taskboard: 'projectBuilder.listType.taskBoard',
  'cards-list': 'projectBuilder.listType.cards',
  cardslist: 'projectBuilder.listType.cards',
  'map-list': 'projectBuilder.listType.map',
  maplist: 'projectBuilder.listType.map',
  1: 'projectBuilder.listType.list',
  2: 'projectBuilder.listType.gantt',
  3: 'projectBuilder.listType.calendar',
  4: 'projectBuilder.listType.taskList',
  5: 'projectBuilder.listType.taskBoard',
  6: 'projectBuilder.listType.cards',
  7: 'projectBuilder.listType.map',
};

/** Wire list type may be hyphenated, PascalCase, or the numeric enum. */
export const listTypeMessageId = (type) => {
  if (type == null || type === '') return null;
  const raw = String(type).trim();
  if (LIST_TYPE_LABEL_IDS[raw]) return LIST_TYPE_LABEL_IDS[raw];
  const key = raw.toLowerCase().replace(/[\s_]/g, '-');
  return LIST_TYPE_LABEL_IDS[key] || LIST_TYPE_LABEL_IDS[key.replace(/-/g, '')] || null;
};

export const issueFor = (issues, elementType, id) =>
  (issues || []).find(
    (issue) =>
      issue?.artifactType === elementType &&
      String(issue.artifactId || '').toLowerCase() === String(id || '').toLowerCase(),
  ) || null;

export const isAccessDenied = (error) => {
  if (!error) return false;
  if (error.internalErrorCode === 'CFG-ACCESS-001') return true;
  if (error.response?.status === 403) return true;
  return false;
};

export const isRevisionConflict = (error) => {
  if (!error) return false;
  if (error.response?.status === 409) return true;
  if (String(error.code) === '409') return true;
  return false;
};

export const mapValidationFields = (error) =>
  (error?.validationErrors || [])
    .map((item) => {
      const raw = String(item?.field || '').toLowerCase();
      const name =
        raw === 'name'
          ? 'name'
          : raw === 'description'
          ? 'description'
          : raw === 'isactive'
          ? 'isActive'
          : item?.field;
      const errors = item?.errorMessages || [];
      if (!name || !errors.length) return null;
      return { name, errors };
    })
    .filter(Boolean);

/** Serialize composition writes so two tokens of the same revision never race. */
let writeChain = Promise.resolve();

export function waitWriteTurn() {
  let release;
  const gate = new Promise((resolve) => {
    release = resolve;
  });
  const previous = writeChain;
  writeChain = previous.then(
    () => gate,
    () => gate,
  );
  return previous.then(
    () => release,
    () => release,
  );
}
